/**
 * ArgumentMap
 *
 * A component for displaying interactive argument maps in chat messages
 *
 * Primary responsibilities:
 * - Visualize logical arguments and their relationships
 * - Allow expanding/collapsing of arguments and counterarguments
 * - Support flexible data structures from AI-generated content
 * - Display argument types and strength indicators
 */

import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { IconCheck, IconChevronDown, IconChevronRight, IconX } from '@tabler/icons-react'

export interface ArgumentNode {
  id: string
  text: string
  type: 'claim' | 'premise' | 'objection' | 'rebuttal' | 'question' | string
  strength?: 'strong' | 'moderate' | 'weak' | number
  children?: ArgumentNode[]
  description?: string
  evidence?: string
  source?: string
  [key: string]: any // Allow for additional custom properties
}

interface ArgumentMapProps {
  title?: string
  rootNode: ArgumentNode
  className?: string
  layout?: 'standard' | 'compact' | 'detailed'
}

export const ArgumentMap: React.FC<ArgumentMapProps> = ({
  title = 'Argument Map',
  rootNode,
  className,
  layout = 'standard'
}) => {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set([rootNode.id]))

  const toggleNode = (nodeId: string) => {
    const newExpandedNodes = new Set(expandedNodes)
    if (newExpandedNodes.has(nodeId)) {
      newExpandedNodes.delete(nodeId)
    } else {
      newExpandedNodes.add(nodeId)
    }
    setExpandedNodes(newExpandedNodes)
  }

  const expandAll = () => {
    const collectAllIds = (node: ArgumentNode): string[] => {
      const ids = [node.id]
      if (node.children) {
        node.children.forEach((child) => {
          ids.push(...collectAllIds(child))
        })
      }
      return ids
    }

    const allIds = collectAllIds(rootNode)
    setExpandedNodes(new Set(allIds))
  }

  const collapseAll = () => {
    // Keep only the root expanded
    setExpandedNodes(new Set([rootNode.id]))
  }

  const getNodeColorClass = (node: ArgumentNode) => {
    // If node has custom style, use that
    if (node.style) return node.style

    // Otherwise use type-based styles
    switch (node.type) {
      case 'claim':
        return 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
      case 'premise':
        return 'border-green-400 bg-green-50 dark:bg-green-900/20'
      case 'objection':
        return 'border-red-400 bg-red-50 dark:bg-red-900/20'
      case 'rebuttal':
        return 'border-purple-400 bg-purple-50 dark:bg-purple-900/20'
      case 'question':
        return 'border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20'
      default:
        // Support custom types with fallback styling
        return 'border-gray-400 bg-gray-50 dark:bg-gray-900/20'
    }
  }

  const getNodeIcon = (node: ArgumentNode) => {
    // Allow custom icons if provided
    if (node.icon) return node.icon

    // Different strength indicators based on type
    if (node.type === 'premise' || node.type === 'rebuttal') {
      if (node.strength !== undefined) {
        if (
          node.strength === 'strong' ||
          node.strength === 3 ||
          (typeof node.strength === 'number' && node.strength >= 0.7)
        ) {
          return <IconCheck className="text-green-500" size={16} />
        }
        if (
          node.strength === 'weak' ||
          node.strength === 1 ||
          (typeof node.strength === 'number' && node.strength <= 0.3)
        ) {
          return <IconX className="text-red-500" size={16} />
        }
      }
    }

    return null
  }

  const renderNode = (node: ArgumentNode, level: number = 0) => {
    const isExpanded = expandedNodes.has(node.id)
    const hasChildren = node.children && node.children.length > 0
    const isDetailed = layout === 'detailed'

    return (
      <div key={node.id} className="mb-3">
        <div
          className={cn(
            'flex items-start gap-2 rounded-md border-2 p-3 transition-colors',
            getNodeColorClass(node),
            hasChildren ? 'cursor-pointer' : ''
          )}
          onClick={() => hasChildren && toggleNode(node.id)}
        >
          {hasChildren && (
            <div className="mt-1">
              {isExpanded ? (
                <IconChevronDown size={16} className="text-gray-500" />
              ) : (
                <IconChevronRight size={16} className="text-gray-500" />
              )}
            </div>
          )}

          <div className="flex-1">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  {level === 0 && (
                    <span className="text-muted-foreground text-xs uppercase tracking-wider">
                      {node.type}
                    </span>
                  )}
                  <span className="text-sm font-medium">{node.text}</span>
                </div>

                {node.description && isDetailed && (
                  <p className="text-muted-foreground mt-1 text-xs">{node.description}</p>
                )}

                {node.evidence && isDetailed && (
                  <div className="mt-2 text-xs">
                    <span className="font-medium">Evidence: </span>
                    <span className="text-muted-foreground">{node.evidence}</span>
                  </div>
                )}

                {node.source && isDetailed && (
                  <div className="mt-1 text-xs">
                    <span className="font-medium">Source: </span>
                    <span className="text-muted-foreground">{node.source}</span>
                  </div>
                )}
              </div>

              <div className="shrink-0">{getNodeIcon(node)}</div>
            </div>
          </div>
        </div>

        {hasChildren && isExpanded && (
          <div className={cn('ml-4 mt-2 pl-8', layout === 'compact' ? 'pl-4' : '')}>
            {node.children?.map((child) => (
              <div
                key={child.id}
                className={cn(
                  'relative mt-2 border-l-2 border-dashed border-gray-300 pl-4 pt-2 dark:border-gray-700',
                  layout === 'compact' ? 'pl-3 pt-1' : ''
                )}
              >
                {renderNode(child, level + 1)}
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'border-border bg-card my-4 flex flex-col overflow-hidden rounded-lg border shadow-sm transition-all',
        className
      )}
    >
      <div className="flex items-center justify-between border-b p-3">
        <div className="flex items-center gap-2">
          <IconCheck size={16} className="text-muted-foreground" />
          <h3 className="text-sm font-medium">{title}</h3>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={expandAll} className="text-xs">
            Expand All
          </Button>
          <Button variant="ghost" size="sm" onClick={collapseAll} className="text-xs">
            Collapse All
          </Button>
        </div>
      </div>

      <div className="p-4">{renderNode(rootNode)}</div>
    </div>
  )
}
